import { useMemo, useState } from 'react';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import type {
  IntakeHubRequirementDefinitionResponse,
  IntakeHubServiceDefinitionResponse,
} from '@store/generated/intake-hub';

import {
  useGetAdminIntakeHubDefinitionsQuery,
  useUpsertAdminIntakeHubRequirementDefinitionMutation,
  useUpsertAdminIntakeHubServiceDefinitionMutation,
} from './store/api';

type EditorMode = 'service' | 'requirement';

interface ServiceFormState {
  serviceKey: string;
  label: string;
  description: string;
  status: 'active' | 'deprecated';
  displayOrder: string;
  metadataJson: string;
}

interface RequirementFormState {
  requirementKey: string;
  serviceKey: string;
  conceptKey: string;
  requirementContext: 'provider' | 'organization' | 'service' | 'payer';
  itemType: 'information' | 'document' | 'action' | 'staff_task' | 'review';
  title: string;
  description: string;
  requiredWhenJson: string;
  displayOrder: string;
  status: 'active' | 'inactive';
  metadataJson: string;
}

function prettyJson(value: unknown) {
  return JSON.stringify(value ?? {}, null, 2);
}

function parseJsonObject(value: string, label: string) {
  try {
    const parsed = value.trim() ? JSON.parse(value) : {};
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error(`${label} must be a JSON object.`);
    }
    return parsed as Record<string, unknown>;
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error(`${label} must be valid JSON.`);
  }
}

function serviceToForm(service?: IntakeHubServiceDefinitionResponse): ServiceFormState {
  return {
    serviceKey: service?.service_key ?? '',
    label: service?.label ?? '',
    description: service?.description ?? '',
    status: service?.status ?? 'active',
    displayOrder: String(service?.display_order ?? 0),
    metadataJson: prettyJson(service?.metadata),
  };
}

function requirementToForm(
  requirement?: IntakeHubRequirementDefinitionResponse,
): RequirementFormState {
  return {
    requirementKey: requirement?.requirement_key ?? '',
    serviceKey: requirement?.service_key ?? '',
    conceptKey: requirement?.concept_key ?? '',
    requirementContext: requirement?.requirement_context ?? 'provider',
    itemType: requirement?.item_type ?? 'document',
    title: requirement?.title ?? '',
    description: requirement?.description ?? '',
    requiredWhenJson: prettyJson(requirement?.required_when),
    displayOrder: String(requirement?.display_order ?? 0),
    status: requirement?.status ?? 'active',
    metadataJson: prettyJson(requirement?.metadata),
  };
}

export function AdminIntakeHubDefinitionsPanel() {
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, isFetching, refetch } = useGetAdminIntakeHubDefinitionsQuery();
  const [upsertService, { isLoading: isSavingService }] =
    useUpsertAdminIntakeHubServiceDefinitionMutation();
  const [upsertRequirement, { isLoading: isSavingRequirement }] =
    useUpsertAdminIntakeHubRequirementDefinitionMutation();
  const [mode, setMode] = useState<EditorMode | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [serviceForm, setServiceForm] = useState<ServiceFormState>(() => serviceToForm());
  const [requirementForm, setRequirementForm] = useState<RequirementFormState>(() =>
    requirementToForm(),
  );

  const services = useMemo(
    () =>
      [...(data?.services ?? [])].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)),
    [data?.services],
  );
  const requirements = useMemo(
    () =>
      [...(data?.requirements ?? [])].sort(
        (a, b) =>
          (a.display_order ?? 0) - (b.display_order ?? 0) ||
          (a.service_key ?? '').localeCompare(b.service_key ?? '') ||
          a.requirement_key.localeCompare(b.requirement_key),
      ),
    [data?.requirements],
  );

  const openServiceEditor = (service?: IntakeHubServiceDefinitionResponse) => {
    setIsCreating(!service);
    setServiceForm(serviceToForm(service));
    setMode('service');
  };

  const openRequirementEditor = (requirement?: IntakeHubRequirementDefinitionResponse) => {
    setIsCreating(!requirement);
    setRequirementForm(requirementToForm(requirement));
    setMode('requirement');
  };

  const closeEditor = () => {
    setMode(null);
    setIsCreating(false);
  };

  const handleSaveService = async () => {
    if (!serviceForm.serviceKey.trim() || !serviceForm.label.trim()) {
      enqueueSnackbar('Service key and label are required.', { variant: 'warning' });
      return;
    }
    try {
      const metadata = parseJsonObject(serviceForm.metadataJson, 'Metadata');
      await upsertService({
        serviceKey: serviceForm.serviceKey.trim(),
        upsertIntakeHubServiceDefinitionRequest: {
          label: serviceForm.label.trim(),
          description: serviceForm.description.trim() || null,
          status: serviceForm.status,
          display_order: Number(serviceForm.displayOrder || 0),
          metadata,
        },
      }).unwrap();
      enqueueSnackbar('Service definition saved.', { variant: 'success' });
      closeEditor();
      await refetch();
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to save service.', {
        variant: 'error',
      });
    }
  };

  const handleSaveRequirement = async () => {
    if (!requirementForm.requirementKey.trim() || !requirementForm.title.trim()) {
      enqueueSnackbar('Requirement key and title are required.', { variant: 'warning' });
      return;
    }
    try {
      const requiredWhen = parseJsonObject(requirementForm.requiredWhenJson, 'Required when');
      const metadata = parseJsonObject(requirementForm.metadataJson, 'Metadata');
      await upsertRequirement({
        requirementKey: requirementForm.requirementKey.trim(),
        upsertIntakeHubRequirementDefinitionRequest: {
          service_key: requirementForm.serviceKey.trim() || null,
          concept_key: requirementForm.conceptKey.trim() || null,
          requirement_context: requirementForm.requirementContext,
          item_type: requirementForm.itemType,
          title: requirementForm.title.trim(),
          description: requirementForm.description.trim() || null,
          required_when: requiredWhen,
          display_order: Number(requirementForm.displayOrder || 0),
          status: requirementForm.status,
          metadata,
        },
      }).unwrap();
      enqueueSnackbar('Requirement definition saved.', { variant: 'success' });
      closeEditor();
      await refetch();
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to save requirement.', {
        variant: 'error',
      });
    }
  };

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' } }}
      >
        <Box>
          <Typography variant="h6">Definition catalog</Typography>
          <Typography variant="body2" color="text.secondary">
            Live service and requirement definitions that drive new Intake Hub items.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddOutlinedIcon />}
            onClick={() => openServiceEditor()}
          >
            Service
          </Button>
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => openRequirementEditor()}
          >
            Requirement
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined">
        <Box sx={{ p: 2, borderBottom: theme => `1px solid ${theme.palette.divider}` }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Services
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Order</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {services.map(service => (
                <TableRow key={service.service_key} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {service.service_key}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {service.label}
                    </Typography>
                    {service.description && (
                      <Typography variant="caption" color="text.secondary">
                        {service.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={service.status ?? 'active'}
                      color={service.status === 'deprecated' ? 'warning' : 'success'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">{service.display_order ?? 0}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openServiceEditor(service)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!isLoading && services.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      No services defined.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper variant="outlined">
        <Box sx={{ p: 2, borderBottom: theme => `1px solid ${theme.palette.divider}` }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Requirements
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Context</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requirements.map(requirement => (
                <TableRow key={requirement.requirement_key} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {requirement.requirement_key}
                    </Typography>
                    {requirement.concept_key && (
                      <Typography variant="caption" color="text.secondary">
                        {requirement.concept_key}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {requirement.title}
                    </Typography>
                    {requirement.description && (
                      <Typography variant="caption" color="text.secondary">
                        {requirement.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{requirement.service_key ?? 'shared'}</TableCell>
                  <TableCell>{requirement.requirement_context}</TableCell>
                  <TableCell>{requirement.item_type}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={requirement.status ?? 'active'}
                      color={requirement.status === 'inactive' ? 'default' : 'success'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openRequirementEditor(requirement)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!isLoading && requirements.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      No requirements defined.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={mode === 'service'} onClose={closeEditor} fullWidth maxWidth="sm">
        <DialogTitle>{isCreating ? 'Create service' : 'Edit service'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Service key"
              value={serviceForm.serviceKey}
              disabled={!isCreating}
              onChange={event =>
                setServiceForm(current => ({ ...current, serviceKey: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Label"
              value={serviceForm.label}
              onChange={event =>
                setServiceForm(current => ({ ...current, label: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Description"
              value={serviceForm.description}
              onChange={event =>
                setServiceForm(current => ({ ...current, description: event.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={serviceForm.status}
                  onChange={event =>
                    setServiceForm(current => ({
                      ...current,
                      status: event.target.value as ServiceFormState['status'],
                    }))
                  }
                >
                  <MenuItem value="active">active</MenuItem>
                  <MenuItem value="deprecated">deprecated</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Display order"
                type="number"
                value={serviceForm.displayOrder}
                onChange={event =>
                  setServiceForm(current => ({ ...current, displayOrder: event.target.value }))
                }
                fullWidth
              />
            </Stack>
            <TextField
              label="Metadata JSON"
              value={serviceForm.metadataJson}
              onChange={event =>
                setServiceForm(current => ({ ...current, metadataJson: event.target.value }))
              }
              fullWidth
              multiline
              minRows={5}
              spellCheck={false}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor}>Cancel</Button>
          <Button
            variant="contained"
            disabled={isSavingService}
            onClick={() => void handleSaveService()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={mode === 'requirement'} onClose={closeEditor} fullWidth maxWidth="md">
        <DialogTitle>{isCreating ? 'Create requirement' : 'Edit requirement'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Requirement key"
              value={requirementForm.requirementKey}
              disabled={!isCreating}
              onChange={event =>
                setRequirementForm(current => ({ ...current, requirementKey: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Title"
              value={requirementForm.title}
              onChange={event =>
                setRequirementForm(current => ({ ...current, title: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Description"
              value={requirementForm.description}
              onChange={event =>
                setRequirementForm(current => ({ ...current, description: event.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Service key"
                value={requirementForm.serviceKey}
                onChange={event =>
                  setRequirementForm(current => ({ ...current, serviceKey: event.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Concept key"
                value={requirementForm.conceptKey}
                onChange={event =>
                  setRequirementForm(current => ({ ...current, conceptKey: event.target.value }))
                }
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Context</InputLabel>
                <Select
                  label="Context"
                  value={requirementForm.requirementContext}
                  onChange={event =>
                    setRequirementForm(current => ({
                      ...current,
                      requirementContext: event.target
                        .value as RequirementFormState['requirementContext'],
                    }))
                  }
                >
                  <MenuItem value="provider">provider</MenuItem>
                  <MenuItem value="organization">organization</MenuItem>
                  <MenuItem value="service">service</MenuItem>
                  <MenuItem value="payer">payer</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={requirementForm.itemType}
                  onChange={event =>
                    setRequirementForm(current => ({
                      ...current,
                      itemType: event.target.value as RequirementFormState['itemType'],
                    }))
                  }
                >
                  <MenuItem value="information">information</MenuItem>
                  <MenuItem value="document">document</MenuItem>
                  <MenuItem value="action">action</MenuItem>
                  <MenuItem value="staff_task">staff_task</MenuItem>
                  <MenuItem value="review">review</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={requirementForm.status}
                  onChange={event =>
                    setRequirementForm(current => ({
                      ...current,
                      status: event.target.value as RequirementFormState['status'],
                    }))
                  }
                >
                  <MenuItem value="active">active</MenuItem>
                  <MenuItem value="inactive">inactive</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Display order"
                type="number"
                value={requirementForm.displayOrder}
                onChange={event =>
                  setRequirementForm(current => ({ ...current, displayOrder: event.target.value }))
                }
                fullWidth
              />
            </Stack>
            <TextField
              label="Required when JSON"
              value={requirementForm.requiredWhenJson}
              onChange={event =>
                setRequirementForm(current => ({
                  ...current,
                  requiredWhenJson: event.target.value,
                }))
              }
              fullWidth
              multiline
              minRows={5}
              spellCheck={false}
            />
            <TextField
              label="Metadata JSON"
              value={requirementForm.metadataJson}
              onChange={event =>
                setRequirementForm(current => ({ ...current, metadataJson: event.target.value }))
              }
              fullWidth
              multiline
              minRows={5}
              spellCheck={false}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor}>Cancel</Button>
          <Button
            variant="contained"
            disabled={isSavingRequirement}
            onClick={() => void handleSaveRequirement()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
